(function () {
  if (!customElements.get('media-gallery')) {
    const _isDev =
      window.location.hostname === 'localhost' ||
      new URLSearchParams(window.location.search).has('preview_theme_id');

    customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        constructor() {
          super();

          this.selectors = {
            viewer: '[id^="GalleryViewer"]',
            thumbnails: '[id^="GalleryThumbnails"]',
            mediaList: '[id^="Slider-Gallery"]',
            mediaItems: ['.product__media-item'],
          };

          this.sliderInstance = false;
          this.thumbsInstance = false;
          this.slideHeights = new Map(); // Store pre-calculated heights for smooth transitions
          this.imageLoadListeners = new Map(); // Track image load listeners for cleanup
          this.imageLoadTimeouts = new Map(); // Track image load timeout IDs for cleanup
          this.swiperEventHandlers = []; // Track Swiper event handlers for cleanup
        }

        connectedCallback() {
          this.init();
        }

        init() {
          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
          this.mediaLayout = this.dataset.mediaLayout;
          this.onlyImage = this.dataset.onlyImage === 'true';
          this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
          this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
          this.enableImageZoom = this.dataset.enableImageZoom === 'true';
          this.setSliderOptions();

          this.mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          this.boundUpdateMediaLayout = this.updateMediaLayout.bind(this);
          this.mql.addEventListener('change', this.boundUpdateMediaLayout);
          this.updateMediaLayout();

          if (this.enableImageZoom) {
            this.initImageZoom();
          }

          this.initShowAllButton();
        }

        initShowAllButton() {
          const showAllButton = this.querySelector('.product__media-gallery-show-all');
          if (!showAllButton) return;

          const mediaItems = this.querySelectorAll('[id^="Slider-Gallery"] .product__media-item');
          if (mediaItems.length === 0) return;

          const handleShowAllClick = () => {
            this.openGalleryModal(mediaItems);
          };

          showAllButton.addEventListener('click', handleShowAllClick);

          // Track for cleanup
          if (!this.clickHandlers) {
            this.clickHandlers = [];
          }
          this.clickHandlers.push({ element: showAllButton, handler: handleShowAllClick });
        }

        openGalleryModal(mediaItems) {
          // Create modal overlay
          const modal = document.createElement('div');
          modal.className = 'product__media-gallery-modal';
          modal.setAttribute('role', 'dialog');
          modal.setAttribute('aria-modal', 'true');

          // Create close button
          const closeBtn = document.createElement('button');
          closeBtn.className = 'product__media-gallery-modal-close';
          closeBtn.setAttribute('type', 'button');
          closeBtn.setAttribute('aria-label', 'Close gallery');
          closeBtn.textContent = '×';

          // Create grid container
          const grid = document.createElement('div');
          grid.className = 'product__media-gallery-modal-grid';

          // Add thumbnail for each media item
          mediaItems.forEach((mediaItem, index) => {
            const img = mediaItem.querySelector('img');
            if (!img) return;

            const gridItem = document.createElement('div');
            gridItem.className = 'product__media-gallery-modal-item';

            const imgClone = img.cloneNode(true);
            imgClone.style.width = '100%';
            imgClone.style.height = '100%';
            imgClone.style.objectFit = 'cover';

            gridItem.appendChild(imgClone);
            gridItem.style.cursor = 'pointer';

            gridItem.addEventListener('click', () => {
              // Use Swiper API directly to slide to the selected image
              // This triggers realIndexChange handler which syncs thumbs, height, and media playback
              if (this.sliderInstance && this.sliderInstance.slider) {
                this.sliderInstance.slider.slideTo(index, 0, false);
              }
              // Close modal
              modal.remove();
            });

            grid.appendChild(gridItem);
          });

          modal.appendChild(closeBtn);
          modal.appendChild(grid);

          // Close on close button
          closeBtn.addEventListener('click', () => {
            modal.remove();
          });

          // Close on outside click
          modal.addEventListener('click', (e) => {
            if (e.target === modal) {
              modal.remove();
            }
          });

          // Close on ESC key
          const handleEscape = (e) => {
            if (e.key === 'Escape') {
              modal.remove();
              document.removeEventListener('keydown', handleEscape);
            }
          };
          document.addEventListener('keydown', handleEscape);

          document.body.appendChild(modal);
        }

        disconnectedCallback() {
          if (this.mql && this.boundUpdateMediaLayout) {
            this.mql.removeEventListener('change', this.boundUpdateMediaLayout);
          }

          if (this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
          }

          if (this.lightboxDataSource) {
            this.lightboxDataSource = null;
          }

          if (this._zoomClickHandler) {
            this.removeEventListener('click', this._zoomClickHandler, true);
            this._zoomClickHandler = null;
          }

          // Clean up image load listeners and timeouts to prevent memory leaks
          if (this.imageLoadListeners && this.imageLoadListeners.size > 0) {
            this.imageLoadListeners.forEach(({ loadHandler, errorHandler }, img) => {
              img.removeEventListener('load', loadHandler);
              img.removeEventListener('error', errorHandler);
            });
            this.imageLoadListeners.clear();
          }

          // Clear any pending image load timeouts
          if (this.imageLoadTimeouts && this.imageLoadTimeouts.size > 0) {
            this.imageLoadTimeouts.forEach((timeoutId) => {
              clearTimeout(timeoutId);
            });
            this.imageLoadTimeouts.clear();
          }

          // Clean up Swiper event handlers to prevent memory leaks
          if (
            this.sliderInstance &&
            typeof this.sliderInstance === 'object' &&
            this.swiperEventHandlers.length > 0
          ) {
            this.swiperEventHandlers.forEach(({ event, handler }) => {
              this.sliderInstance.slider.off(event, handler);
            });
            this.swiperEventHandlers = [];
          }

          // Clear slide heights Map to free memory
          if (this.slideHeights) {
            this.slideHeights.clear();
          }

          if (this.thumbsInstance && typeof this.thumbsInstance === 'object') {
            this.thumbsInstance.slider.destroy();
            this.thumbsInstance = false;
          }

          if (this.sliderInstance && typeof this.sliderInstance === 'object') {
            this.sliderInstance.slider.destroy();
            this.sliderInstance = false;
          }

          // Clean up Show All button click handlers
          if (this.clickHandlers && this.clickHandlers.length > 0) {
            this.clickHandlers.forEach(({ element, handler }) => {
              element.removeEventListener('click', handler);
            });
            this.clickHandlers = [];
          }
        }

        setSliderOptions() {
          const mediaItemGap = parseInt(this.dataset.mediaItemGap, 10);

          // iOS detection - ALL iOS browsers use Safari's WebKit engine (Apple requirement)
          // Chrome, Firefox, Edge on iOS are all WebKit with different UI, same GPU limits
          // Loop mode causes DOM explosion (30 slides) exceeding mobile GPU limits
          const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

          const isMobileSafari = isIOS; // All iOS browsers are effectively mobile Safari (WebKit)
          // Samsung Internet misidentifies passive listener requirements, causing touchmove suppression
          const isSamsungBrowser = /SamsungBrowser/.test(navigator.userAgent);

          this.sliderOptions = {
            init: false,
            slidesPerView: '1',
            spaceBetween: mediaItemGap,
            loop: !isIOS, // Disable loop on ALL iOS browsers - prevents 30 slide DOM explosion
            loopFillGroupWithBlank: false, // Prevent blank slides when looping
            grabCursor: !isMobileSafari,
            allowTouchMove: true,
            autoHeight: false, // Disable Swiper autoHeight - conflicts with custom height system
            navigation: {
              nextEl: this.querySelector('.swiper-button-next'),
              prevEl: this.querySelector('.swiper-button-prev'),
            },
            pagination: {
              el: this.querySelector('.swiper-pagination'),
              clickable: true,
              type: 'fraction',
            },
            threshold: 2,

            // iOS-specific optimizations (all iOS browsers use WebKit)
            ...(isIOS && {
              observer: true,
              observeParents: true,
              observeSlideChildren: false,
              resistanceRatio: 0.85,
              longSwipesRatio: 0.3,
              watchSlidesProgress: false,
            }),

            // Samsung Internet: registers touch handlers as passive by default,
            // which causes the browser to suppress touchmove and break swiping
            ...(isSamsungBrowser && {
              touchEventsTarget: 'wrapper',
              passiveListeners: false,
              threshold: 5,
            }),
          };

          // Calculate slidesPerView based on the swiper container width.
          // #GalleryThumbnails is now the inner swiper element only (buttons are outside),
          // so its offsetWidth equals the actual slide area — no button deduction needed.
          const thumbsContainer = this.querySelector('[id^="GalleryThumbnails"]');
          const thumbsViewportWidth = thumbsContainer ? thumbsContainer.offsetWidth : 0;
          const gap = 10; // --media-item-gap
          // N slides fit when each is at least 50px: N ≤ (W + gap) / (50 + gap)
          // Cap at 5 (design target). If W=0 (pre-layout), fall back to 5 — update() corrects later.
          const slidesPerView =
            thumbsViewportWidth > 0
              ? Math.min(5, Math.floor((thumbsViewportWidth + gap) / (50 + gap)))
              : 5;

          this.thumbsOptions = {
            slidesPerView: Math.max(slidesPerView, 3), // Minimum 3 slides
            spaceBetween: gap, // Swiper must own spacing for correct scroll math; matches CSS gap
            loop: false,
            loopFillGroupWithBlank: false,
            freeMode: false,
            watchSlidesProgress: true,
            watchSlidesVisibility: true,
            observeSlides: true,
            threshold: 2,
            allowTouchMove: false,
            on: {
              init: (swiper) => {
                // Wait for thumbnail images to load before calculating scroll distance
                const images =
                  this.elements.thumbnails && this.elements.thumbnails.querySelectorAll('img');
                let loadedCount = 0;

                const onImageLoad = () => {
                  loadedCount++;
                  if (loadedCount >= images.length) {
                    swiper.update();
                  }
                };

                if (images && images.length > 0) {
                  images.forEach((img) => {
                    if (img.complete) {
                      loadedCount++;
                    } else {
                      img.addEventListener('load', onImageLoad);
                      img.addEventListener('error', onImageLoad);
                    }
                  });

                  // If all images were already loaded (cached), update immediately
                  if (loadedCount >= images.length) {
                    swiper.update();
                  }
                  // Fallback: update after 500ms if images haven't loaded
                  else {
                    setTimeout(() => swiper.update(), 500);
                  }
                } else {
                  setTimeout(() => swiper.update(), 100);
                }
              },
            },
            navigation: {
              // Buttons are siblings of #GalleryThumbnails inside .product__media-gallery-thumbails-wrap
              nextEl:
                this.elements.thumbnails &&
                this.elements.thumbnails.parentElement &&
                this.elements.thumbnails.parentElement.querySelector('.swiper-button-next'),
              prevEl:
                this.elements.thumbnails &&
                this.elements.thumbnails.parentElement &&
                this.elements.thumbnails.parentElement.querySelector('.swiper-button-prev'),
            },
          };

          switch (this.mediaLayout) {
            case 'vertical-carousel':
              this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
                breakpoints: {
                  768: {
                    direction: 'vertical',
                    slidesPerView: 'auto',
                  },
                },
              });
              break;
            case 'slider-freemode':
              this.sliderOptions = Object.assign({}, this.sliderOptions, {
                slidesPerView: 'auto',
              });
              break;
          }
        }

        updateMediaLayout() {
          if (FoxTheme.config.mqlMobile) {
            this.initSlider();
          } else {
            if (this.enableDesktopSlider) {
              this.initSlider();
            } else {
              this.destroySlider();
            }
          }
        }

        initSlider() {
          if (typeof this.sliderInstance !== 'object') {
            if (
              (this.enableDesktopSlider || this.enableMobileThumbnails) &&
              this.elements.thumbnails
            ) {
              this.thumbsInstance = new window.FoxTheme.Carousel(
                this.elements.thumbnails,
                this.thumbsOptions
              );
              this.thumbsInstance.init();

              this.sliderOptions.thumbs = {
                swiper: this.thumbsInstance.slider,
                autoScrollOffset: 2,
              };
            }

            this.sliderInstance = new window.FoxTheme.Carousel(
              this.elements.viewer,
              this.sliderOptions,
              [FoxTheme.Swiper.Thumbs]
            );
            this.sliderInstance.init();

            this.handleSliderAfterInit();
            this.handleSlideChange();

            this.sliderInstance.slider.init();
          }
        }

        destroySlider() {
          if (typeof this.sliderInstance === 'object') {
            this.sliderInstance.slider.destroy();
            this.sliderInstance = false;
          }
        }

        initThumbsSlider() {
          if (typeof this.thumbsInstance !== 'object') {
            this.thumbsInstance = new window.FoxTheme.Carousel(
              this.selectors.thumbnails,
              this.thumbsOptions
            );
            this.thumbsInstance.init();
          }
        }

        initImageZoom() {
          this.lightboxDataSource = [];
          const allMedia = [
            ...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)'),
          ];
          if (allMedia) {
            allMedia.forEach((media) => {
              switch (media.dataset.mediaType) {
                case 'model':
                  this.lightboxDataSource.push({
                    id: media.dataset.mediaIndex,
                    html: `<div class="pswp__item--${media.dataset.mediaType}">${
                      media.querySelector('product-model').outerHTML
                    }</div>`,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
                case 'video':
                case 'external_video':
                  this.lightboxDataSource.push({
                    id: media.dataset.mediaIndex,
                    html: `<div class="pswp__item--${media.dataset.mediaType}">${
                      media.querySelector('video-element').outerHTML
                    }</div>`,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
                case 'image':
                  this.lightboxDataSource.push({
                    id: media.dataset.mediaIndex,
                    src: media.dataset.src,
                    width: media.dataset.pswpWidth,
                    height: media.dataset.pswpHeight,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
              }
            });
          }

          this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
            dataSource: this.lightboxDataSource,
            pswpModule: window.FoxTheme.PhotoSwipe,
            bgOpacity: 1,
            arrowPrev: false,
            arrowNext: false,
            zoom: false,
            close: false,
            counter: false,
            preloader: false,
          });

          this.lightbox.addFilter('thumbEl', (thumbEl, { id: _id }, _index) => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { slides, activeIndex } = this.sliderInstance.slider;
              if (slides && slides[activeIndex]) {
                const el = slides[activeIndex].querySelector('img');
                if (el) {
                  return el;
                }
              }
            }

            return thumbEl;
          });

          this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id: _id } }) => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { slides, activeIndex } = this.sliderInstance.slider;
              if (slides[activeIndex]) {
                const el = slides[activeIndex].querySelector('img');
                if (el) {
                  return el.src;
                }
              }
            }

            return placeholderSrc;
          });

          // Sync slider position when lightbox closes (not during navigation)
          // Using closingAnimationStart prevents constant syncing during swiping
          this.lightbox.on('closingAnimationStart', () => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { currIndex } = this.lightbox.pswp;
              // CRITICAL: Find slide by media ID, not index
              // Reason: Lightbox dataSource excludes Swiper duplicates (non-iOS loop mode)
              // but slider has duplicates in the DOM, so indices don't match.
              // Example: lightbox currIndex=2 might map to slider slide 5 due to duplicates.
              const currentSlide = this.lightboxDataSource[currIndex];
              if (currentSlide) {
                const mediaIndex = String(currentSlide.id);
                const slides = this.sliderInstance.slider.slides;
                let slideIndex = -1;
                for (let i = 0; i < slides.length; i++) {
                  if (slides[i].dataset && slides[i].dataset.mediaIndex === mediaIndex) {
                    slideIndex = i;
                    break;
                  }
                }
                if (slideIndex !== -1) {
                  // Use slideTo() instead of slideToLoop() - more reliable, fewer bugs
                  // See: https://github.com/nolimits4web/swiper/issues/2942
                  this.sliderInstance.slider.slideTo(slideIndex, 0, false);
                }
              }
            }
          });

          // Pause media during lightbox navigation
          this.lightbox.on('change', () => {
            window.pauseAllMedia(this);
          });

          this.lightbox.on('pointerDown', (e) => {
            if (this.lightbox.pswp.currSlide.data.mediaType !== 'image') {
              e.preventDefault();
            }
          });

          this.lightbox.on('uiRegister', () => {
            if (!this.onlyImage) {
              this.lightbox.pswp.ui.registerElement({
                name: 'next',
                ariaLabel: 'Next slide',
                order: 3,
                isButton: true,
                html: '<svg class="pswp-icon-next" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" transform="translate(100, 100) rotate(180) "></path></svg>',
                onClick: (_event, _el) => {
                  this.lightbox.pswp.next();
                },
              });
              this.lightbox.pswp.ui.registerElement({
                name: 'prev',
                ariaLabel: 'Previous slide',
                order: 1,
                isButton: true,
                html: '<svg class="pswp-icon-prev" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
                onClick: (_event, _el) => {
                  this.lightbox.pswp.prev();
                },
              });
            }
            this.lightbox.pswp.ui.registerElement({
              name: 'close-zoom',
              ariaLabel: 'Close zoom image',
              order: 2,
              isButton: true,
              html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
              onClick: (_event, _el) => {
                this.lightbox.pswp.close();
              },
            });
          });

          this.lightbox.init();

          // Zoom click handler - handles opening PhotoSwipe lightbox
          // Use direct event listener with capture phase to ensure we catch clicks
          //
          // IMPORTANT: DOM Structure (from product-thumbnail.liquid):
          //   .product__media-item
          //     ├── motion-element > img  ← User clicks here
          //     ├── .product__media-icon  ← Decorative zoom icon (pointer-events: none)
          //     └── button.js-photoswipe--zoom  ← Actual clickable button (sibling, not parent!)
          //
          // Because the image and zoom button are SIBLINGS (not parent-child),
          // we must find the zoom button via querySelector, not closest().
          //
          const handleZoomClick = (e) => {
            // Check if clicked on zoom button OR on the image/media item itself
            const button = e.target.closest('.js-photoswipe--zoom');
            const mediaItem = e.target.closest('.product__media-item');

            // If no media item found, ignore click
            if (!mediaItem) return;

            // For image clicks, find the sibling zoom button
            const zoomButton = button || mediaItem.querySelector('.js-photoswipe--zoom');
            if (!zoomButton) return;

            // Only handle image type media
            const mediaType = zoomButton.dataset.mediaType || mediaItem.dataset.mediaType;
            if (mediaType !== 'image') return;

            e.preventDefault();
            e.stopPropagation();

            let index;
            if (
              this.sliderInstance &&
              this.sliderInstance.slider &&
              this.sliderInstance.slider.realIndex !== undefined
            ) {
              index = this.sliderInstance.slider.realIndex;
            } else {
              index = Number(zoomButton.dataset.mediaIndex || mediaItem.dataset.mediaIndex) || 0;
            }

            this.lightbox.loadAndOpen(index);
          };

          // Remove any existing handler before adding new one
          if (this._zoomClickHandler) {
            this.removeEventListener('click', this._zoomClickHandler, true);
          }

          // Use capture phase to ensure we catch clicks before other handlers
          this.addEventListener('click', handleZoomClick, true);
          this._zoomClickHandler = handleZoomClick;
        }

        handleSliderAfterInit() {
          // Create bound handler for cleanup
          this.afterInitHandler = (swiper) => {
            const { slides, activeIndex } = swiper;

            if (slides[activeIndex]) {
              const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
              this.toggleSliderDraggableState(!isModelMediaType);
            }

            // Defer height calculation until the browser has laid out the DOM
            // The 'afterInit' event fires before layout, so offsetWidth could be 0
            // Use nested requestAnimationFrame: first RAF starts the frame, second RAF
            // runs after paint/layout is complete, ensuring offsetWidth is accurate
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Pre-calculate slide heights for smooth dynamic sizing
                this.calculateSlideHeights();

                // Set initial height
                this.updateGalleryHeight(activeIndex);
              });
            });
          };

          // Attach handler and track for cleanup
          this.sliderInstance.slider.on('afterInit', this.afterInitHandler);
          this.swiperEventHandlers.push({ event: 'afterInit', handler: this.afterInitHandler });
        }

        calculateSlideHeights() {
          // Pre-calculate all slide heights in a single batch (prevents reflow storms)
          const containerWidth = this.elements.viewer.offsetWidth;

          if (_isDev) {
            // eslint-disable-next-line no-console
            console.log('[MediaGallery] calculateSlideHeights:', {
              containerWidth,
              viewerElement: !!this.elements.viewer,
              viewerOffsetWidth: this.elements.viewer && this.elements.viewer.offsetWidth,
              totalSlides:
                this.sliderInstance &&
                this.sliderInstance.slider &&
                this.sliderInstance.slider.slides &&
                this.sliderInstance.slider.slides.length,
            });
          }

          // Height cap to prevent GPU memory exhaustion on iPhone 16
          // Calculation: 10 slides × (1.2 × viewport height) = safe GPU usage
          // Without cap: 10 slides × 2500px = 25,000px > 20,000px GPU limit ❌
          // With cap: 10 slides × (1.2 × ~900px iPhone) = ~11,000px < 20,000px ✓
          const maxHeight = window.innerHeight * 1.2;

          this.sliderInstance.slider.slides.forEach((slide, index) => {
            const img = slide.querySelector('img');
            if (img) {
              // Check if this is an eager-loaded image (e.g., first product image)
              const isEagerLoaded =
                img.loading === 'eager' || img.getAttribute('loading') === 'eager';

              // Use async decode API to prevent GPU memory spikes on main thread
              if (img.decode && img.complete) {
                img.decode().catch((e) => {
                  if (_isDev) console.error('[MediaGallery] img.decode() failed:', e);
                  // Fallback: Sync decode will happen on render if async fails
                });
              }

              // Robust image loading handler that covers all states
              const handleImageReady = () => {
                // Add .loaded class to trigger CSS opacity transition (critical for lazy-loaded images)
                // Eager-loaded images are already visible via fetchpriority CSS rule, but .loaded class
                // ensures consistency with media-gallery's state management and prevents initialization timing issues
                if (!img.classList.contains('loaded')) {
                  img.classList.add('loaded');
                  if (_isDev) {
                    // eslint-disable-next-line no-console
                    console.log(
                      `[MediaGallery] Slide ${index} image marked as loaded (eager: ${isEagerLoaded})`
                    );
                  }
                }

                // Calculate actual height if dimensions are available
                if (img.naturalWidth && img.naturalHeight) {
                  const aspectRatio = img.naturalHeight / img.naturalWidth;
                  const naturalHeight = containerWidth * aspectRatio;
                  const cappedHeight = Math.min(naturalHeight, maxHeight);
                  this.slideHeights.set(index, cappedHeight);
                  if (_isDev) {
                    // eslint-disable-next-line no-console
                    console.log(`[MediaGallery] Slide ${index} height updated:`, cappedHeight);
                  }

                  // Update height if this is the active slide
                  if (
                    index ===
                    (this.sliderInstance &&
                      this.sliderInstance.slider &&
                      this.sliderInstance.slider.activeIndex)
                  ) {
                    this.updateGalleryHeight(index);
                  }
                }
              };

              // Flag to prevent double-processing if load completes between checks
              let isProcessed = false;

              const markProcessed = () => {
                if (isProcessed) return;
                isProcessed = true;
                return true;
              };

              // Check if image is already loaded (synchronous path)
              if (img.complete && img.naturalWidth && img.naturalHeight) {
                // Image cached or immediately available (common for eager-loaded images)
                if (markProcessed()) {
                  handleImageReady();
                  if (_isDev) {
                    // eslint-disable-next-line no-console
                    console.log(
                      `[MediaGallery] Slide ${index} height (image already loaded):`,
                      this.slideHeights.get(index)
                    );
                  }
                }
              } else {
                // Image is not ready. Only the placeholder height estimate is skipped for
                // eager images: the browser is already fetching them at high priority, so a
                // guessed height would just cause a visible correction moments later. The
                // load, error and timeout handlers below apply to every unready image.
                // This branch used to be `else if (!isEagerLoaded)`, which skipped the
                // handlers too, so an eager image that was merely slow, or one that was
                // `complete` with naturalWidth 0 because it 404'd, fell through with no
                // handling at all: no .loaded class, no error handler, no timeout fallback,
                // and no height entry, and nothing ever recovered it.
                if (!isEagerLoaded) {
                  const pswpW = parseInt(slide.dataset.pswpWidth, 10);
                  const pswpH = parseInt(slide.dataset.pswpHeight, 10);
                  const estimatedHeight =
                    pswpW && pswpH
                      ? Math.min(containerWidth * (pswpH / pswpW), maxHeight)
                      : containerWidth;
                  this.slideHeights.set(index, estimatedHeight);
                  if (_isDev) {
                    // eslint-disable-next-line no-console
                    console.log(`[MediaGallery] Slide ${index} height (estimated):`, {
                      pswpW,
                      pswpH,
                      estimatedHeight,
                    });
                  }
                }

                // Load handler - processes only once
                const loadHandler = () => {
                  if (markProcessed()) {
                    handleImageReady();
                    // Recalculate Swiper scroll distance after image loads
                    if (this.thumbsInstance && this.thumbsInstance.slider) {
                      this.thumbsInstance.slider.update();
                    }
                  }
                  cleanup();
                };

                // Error handler - prevents opacity stuck at 0
                const errorHandler = () => {
                  if (markProcessed()) {
                    img.classList.add('loaded');
                    if (_isDev) {
                      console.warn(`[MediaGallery] Slide ${index} image failed to load:`, img.src);
                    }
                  }
                  cleanup();
                };

                // Timeout handler - fallback for stuck/stalled image loads
                let timeoutId = null;
                const handleTimeout = () => {
                  if (markProcessed()) {
                    img.classList.add('loaded');
                    if (_isDev) {
                      console.warn(
                        `[MediaGallery] Slide ${index} image load timeout (15s):`,
                        img.src
                      );
                    }
                  }
                  cleanup();
                };

                const cleanup = () => {
                  if (timeoutId) {
                    clearTimeout(timeoutId);
                    this.imageLoadTimeouts.delete(img);
                  }
                  img.removeEventListener('load', loadHandler);
                  img.removeEventListener('error', errorHandler);
                  this.imageLoadListeners.delete(img);
                };

                // Attach handlers
                img.addEventListener('load', loadHandler, { once: true });
                img.addEventListener('error', errorHandler, { once: true });

                // Timeout fallback: 15 seconds max wait for image
                timeoutId = setTimeout(handleTimeout, 15000);
                this.imageLoadTimeouts.set(img, timeoutId);

                // Track listeners for cleanup
                this.imageLoadListeners.set(img, { loadHandler, errorHandler });

                // Double-check: image might have loaded between our check and listener attachment
                if (img.complete && img.naturalWidth && img.naturalHeight) {
                  if (markProcessed()) {
                    handleImageReady();
                    cleanup();
                  }
                }
              }
            } else {
              // No image found - use container width as fallback
              this.slideHeights.set(index, containerWidth);
              if (_isDev) {
                console.warn(`[MediaGallery] Slide ${index} has no image element, using fallback`);
              }
            }
          });

          if (_isDev) {
            // eslint-disable-next-line no-console
            console.log(
              '[MediaGallery] slideHeights map:',
              Array.from(this.slideHeights.entries())
            );
          }
        }

        updateGalleryHeight(activeIndex) {
          // Smoothly transition to the active slide's pre-calculated height
          const height = this.slideHeights.get(activeIndex);
          if (height && this.elements.viewer) {
            this.elements.viewer.style.transition = 'height 0.3s ease';
            this.elements.viewer.style.height = `${height}px`;
          }
        }

        handleSlideChange() {
          // Create bound handler for cleanup
          this.realIndexChangeHandler = (swiper) => {
            const { slides, activeIndex, thumbs } = swiper;

            if (thumbs.swiper) {
              thumbs.swiper.slideTo(activeIndex);
            }

            if (slides[activeIndex]) {
              this.playActiveMedia(slides[activeIndex]);

              const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
              this.toggleSliderDraggableState(!isModelMediaType);

              // Update gallery height smoothly for the new slide
              this.updateGalleryHeight(activeIndex);
            }
          };

          // Attach handler and track for cleanup
          this.sliderInstance.slider.on('realIndexChange', this.realIndexChangeHandler);
          this.swiperEventHandlers.push({
            event: 'realIndexChange',
            handler: this.realIndexChangeHandler,
          });
        }

        toggleSliderDraggableState(isDraggable) {
          if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
            this.sliderInstance.slider.allowTouchMove = isDraggable;
          }
        }

        playActiveMedia(selected) {
          const deferredMedia = selected.querySelector('product-model');
          if (deferredMedia) deferredMedia.loadContent(false);
        }

        setActiveMedia(variant) {
          // Early exit if variant has no featured media (some variants don't have images)
          if (
            !variant ||
            !Object.prototype.hasOwnProperty.call(variant, 'featured_media') ||
            !variant.featured_media
          ) {
            return;
          }

          // Check if slider is initialized and ready (both instance and .slider must exist)
          if (
            this.sliderInstance &&
            typeof this.sliderInstance === 'object' &&
            this.sliderInstance.slider
          ) {
            /**
             * ⚠️ CRITICAL: ALWAYS find slides by media ID, NEVER by position index
             *
             * WHY THIS MATTERS:
             * Shopify's featured_media.position does NOT correspond to Swiper slide indices!
             * - Shopify's position is relative to the variant's own media list (1, 2, 3...)
             * - Swiper slides contain ALL product media in the order they appear in HTML
             * - These two orderings can be completely different!
             *
             * HISTORICAL BUG (Jan 2026):
             * ❌ WRONG CODE (caused variants to show incorrect images):
             *    const slideIndex = variant.featured_media.position - 1;
             *    this.sliderInstance.slider.slideTo(slideIndex, 0, false);
             *
             * Result: Variant 1 showed wrong image, Variant 2 showed wrong image, etc.
             * because position 1 != slide index 0, position 2 != slide index 1
             *
             * ✅ CORRECT APPROACH:
             * 1. Loop through ALL slides in the Swiper instance
             * 2. Find the slide where data-media-id matches variant.featured_media.id
             * 3. Use that slide's actual array index for slideTo()
             *
             * EXAMPLE FROM LIVE PRODUCT:
             * Variant 1: featured_media.id = 36345138872540, position = 1
             *   → Found at slides[1] (not slides[0]!)
             * Variant 3: featured_media.id = 36345138970844, position = 2
             *   → Found at slides[2] (happens to match, but not guaranteed!)
             *
             * DO NOT CHANGE THIS TO USE POSITION - IT WILL BREAK IMAGE SWITCHING
             */
            let slideIndex = -1;

            // Search for slide with matching media ID
            const slides = this.sliderInstance.slider.slides;
            for (let i = 0; i < slides.length; i++) {
              if (
                slides[i].dataset &&
                slides[i].dataset.mediaId === String(variant.featured_media.id)
              ) {
                slideIndex = i;
                break;
              }
            }

            // Fallback: If no match found (shouldn't happen), use position with 1-to-0 index conversion
            // This fallback exists for safety but should rarely execute
            if (slideIndex === -1) {
              slideIndex = Math.max(0, (variant.featured_media.position || 1) - 1);
            }

            // Use slideTo() instead of slideToLoop() - compatible with Safari (loop disabled)
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
          } else {
            this.sortMediaItems(variant);
          }
        }

        sortMediaItems(variant) {
          /**
           * FALLBACK METHOD: Used when slider is not yet initialized
           * This reorders DOM elements directly instead of using slideTo()
           *
           * NOTE: This also uses media ID matching (not position) - same principle as setActiveMedia()
           * The media with matching variant.featured_media.id gets moved to the front of the list
           */
          const newMedias = this.elements.mediaItems;

          // Reset to original ordering first
          newMedias.sort(function (a, b) {
            return a.dataset.mediaIndex - b.dataset.mediaIndex;
          });

          // Find media by ID (NOT position) and move to front
          newMedias.some((media, index) => {
            if (media.dataset.mediaId === String(variant.featured_media.id)) {
              const [element] = newMedias.splice(index, 1);
              newMedias.unshift(element);
              return true;
            }
          });

          this.elements.mediaList.textContent = '';
          newMedias.forEach((media) => {
            this.elements.mediaList.appendChild(media);
          });

          if (!FoxTheme.config.mqlMobile) {
            const selectedMedia = this.querySelector(
              `[data-media-id="${variant.featured_media.id}"]`
            );
            if (selectedMedia) {
              window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
            }
          }
        }
      }
    );
  }
})();
